import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtDate, fmtDateTime, formatApiErrorDetail } from "@/lib/api";
import { Loader2, CheckCircle2, Plug, X, RefreshCw, Link2, Trash2 } from "lucide-react";

export default function Integrations() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data: s } = await api.get("/qbo/status");
      setStatus(s);
      if (s.connected) {
        const [{ data: a }, { data: m }, { data: h }] = await Promise.all([
          api.get("/qbo/accounts").catch(() => ({ data: [] })),
          api.get("/qbo/mappings"),
          api.get("/qbo/sync-history").catch(() => ({ data: [] })),
        ]);
        setAccounts(a);
        setMappings(m);
        setHistory(h);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load QBO status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (params.get("qbo_connected") === "1") {
      toast.success("QuickBooks connected");
      params.delete("qbo_connected");
      setParams(params, { replace: true });
    } else if (params.get("qbo_error")) {
      toast.error(`QuickBooks connect failed: ${params.get("qbo_error")}`);
      params.delete("qbo_error");
      setParams(params, { replace: true });
    }
  }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/qbo/connect");
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start QuickBooks connect");
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect QuickBooks? Category mappings will also be removed.")) return;
    try {
      await api.post("/qbo/disconnect");
      toast.success("Disconnected");
      load();
    } catch (e) {
      toast.error("Could not disconnect");
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/qbo/sync", {});
      setLastSync(data);
      toast.success(`Synced ${data.synced} · skipped ${data.skipped}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return <div className="text-sm text-ink-light flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <div>
        <div className="overline text-forest">Integrations</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight mt-1">Connect your books.</h1>
        <p className="text-ink-light mt-2 max-w-xl">
          Sync BizCashflow Pros transactions to your QuickBooks Online company. Map categories once, then one-click sync anytime.
        </p>
      </div>

      <div className="card-surface p-6" data-testid="qbo-panel">
        <div className="flex items-start justify-between mb-4 gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#2CA01C]/10 border border-[#2CA01C]/30 flex items-center justify-center text-[#2CA01C] font-bold">QB</div>
            <div>
              <div className="font-heading text-xl font-medium">QuickBooks Online</div>
              <div className="text-xs text-ink-lighter mt-0.5">
                {status?.environment === "sandbox" ? "Sandbox mode" : "Production"} · One-way sync (BizCashflow Pros → QuickBooks)
              </div>
            </div>
          </div>
          {status?.connected ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-forest font-medium bg-forest/10 px-2 py-1 rounded-full" data-testid="qbo-connected-badge">
              <CheckCircle2 className="w-3.5 h-3.5" /> Connected
            </span>
          ) : null}
        </div>

        {!status?.configured && (
          <div className="text-sm text-ink-lighter">QuickBooks is not configured on this server. Ask your admin to add QBO_CLIENT_ID + QBO_CLIENT_SECRET to the backend .env.</div>
        )}

        {status?.configured && !status?.connected && (
          <div className="flex items-center justify-between gap-4 pt-2">
            <div className="text-sm text-ink-light">Click below to authorize BizCashflow Pros to write transactions to your QuickBooks Online company.</div>
            <button onClick={connect} disabled={connecting} data-testid="qbo-connect-button"
              className="btn-forest inline-flex items-center gap-2">
              {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />}
              Connect to QuickBooks
            </button>
          </div>
        )}

        {status?.connected && (
          <div className="pt-2 space-y-6">
            <div className="flex items-center justify-between border-b border-border pb-4">
              <div className="text-sm text-ink-light">
                Realm ID: <span className="num text-xs text-ink">{status.connection?.realm_id}</span>
                <span className="mx-2 text-ink-lighter">·</span>
                Connected {status.connection?.connected_at ? fmtDate(status.connection.connected_at) : "-"}
              </div>
              <button onClick={disconnect} data-testid="qbo-disconnect-button"
                className="text-xs text-ink-lighter hover:text-destructive inline-flex items-center gap-1.5">
                <X className="w-3.5 h-3.5" /> Disconnect
              </button>
            </div>

            <MappingsSection accounts={accounts} mappings={mappings} onSaved={load} />

            <div className="pt-2 border-t border-border">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-medium text-ink">Sync transactions</div>
                  <div className="text-xs text-ink-lighter mt-0.5">Pushes un-synced BizCashflow Pros transactions to QuickBooks using your mappings.</div>
                </div>
                <button onClick={sync} disabled={syncing || mappings.length === 0}
                  data-testid="qbo-sync-button"
                  className="btn-primary inline-flex items-center gap-2">
                  {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Sync now
                </button>
              </div>
              {mappings.length === 0 && (
                <div className="text-xs text-ochre mt-2">Add at least one category mapping before syncing.</div>
              )}
              {lastSync && (
                <div className="mt-3 text-sm bg-paper rounded-lg p-3" data-testid="qbo-sync-result">
                  <div>
                    <span className="font-medium text-forest">{lastSync.synced} synced</span>
                    <span className="mx-2 text-ink-lighter">·</span>
                    <span className="text-ink-light">{lastSync.skipped} skipped</span>
                    {lastSync.errors?.length > 0 && (
                      <span className="mx-2 text-ochre">· {lastSync.errors.length} error{lastSync.errors.length > 1 ? "s" : ""}</span>
                    )}
                  </div>
                  {lastSync.errors?.slice(0, 3).map((e, i) => (
                    <div key={i} className="text-xs text-ink-lighter mt-1">
                      • {e.reason}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <SyncHistorySection history={history} />
          </div>
        )}
      </div>
    </div>
  );
}

function SyncHistorySection({ history }) {
  if (!history || history.length === 0) {
    return (
      <div className="pt-2 border-t border-border">
        <div className="font-medium text-ink mb-1">Sync history</div>
        <div className="text-xs text-ink-lighter">No syncs yet. Run your first sync above.</div>
      </div>
    );
  }
  return (
    <div className="pt-2 border-t border-border" data-testid="qbo-sync-history">
      <div className="font-medium text-ink mb-3">Sync history</div>
      <div className="divide-y divide-border border-t border-border">
        {history.map((r) => (
          <div key={r.id} className="flex items-center justify-between py-2.5 text-sm gap-3" data-testid={`qbo-sync-run-${r.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-ink truncate">{fmtDateTime(r.started_at)}</div>
              {r.errors_count > 0 && r.first_errors?.[0] && (
                <div className="text-xs text-ochre truncate mt-0.5">• {r.first_errors[0].reason}</div>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs whitespace-nowrap">
              <span className="text-forest font-medium">{r.synced} synced</span>
              <span className="text-ink-lighter">{r.skipped} skipped</span>
              {r.errors_count > 0 && (
                <span className="text-ochre">{r.errors_count} error{r.errors_count > 1 ? "s" : ""}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MappingsSection({ accounts, mappings, onSaved }) {
  const [category, setCategory] = useState("");
  const [accountId, setAccountId] = useState("");
  const [saving, setSaving] = useState(false);

  const CATEGORIES = [
    "Revenue", "Software", "Travel", "Meals", "Rent", "Utilities", "Supplies",
    "Marketing", "Insurance", "Contractors", "Education", "Debt Payment",
    "Taxes", "Bank Fees", "Professional Services", "Payroll", "Equipment", "Other",
  ];

  const save = async () => {
    if (!category || !accountId) {
      toast.error("Pick both a category and a QuickBooks account");
      return;
    }
    const acc = accounts.find((a) => a.id === accountId);
    if (!acc) return;
    setSaving(true);
    try {
      await api.post("/qbo/mappings", {
        category,
        qbo_account_id: accountId,
        qbo_account_name: acc.name,
        qbo_account_type: acc.type,
      });
      toast.success("Mapping saved");
      setCategory("");
      setAccountId("");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save mapping");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (cat) => {
    try {
      await api.delete(`/qbo/mappings/${encodeURIComponent(cat)}`);
      toast.success("Removed");
      onSaved();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="qbo-mappings-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-medium text-ink">Category mappings</div>
          <div className="text-xs text-ink-lighter mt-0.5">Map each BizCashflow Pros category to a QuickBooks account.</div>
        </div>
        <div className="text-xs text-ink-lighter">{mappings.length} mapped · {accounts.length} QBO accounts</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr,1fr,auto] gap-2 mb-3">
        <select value={category} onChange={(e) => setCategory(e.target.value)}
          data-testid="qbo-map-category-select" className="input-base">
          <option value="">BizCashflow Pros category…</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={accountId} onChange={(e) => setAccountId(e.target.value)}
          data-testid="qbo-map-account-select" className="input-base">
          <option value="">QuickBooks account…</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>{a.name} ({a.type})</option>
          ))}
        </select>
        <button onClick={save} disabled={saving} data-testid="qbo-map-save-button"
          className="btn-primary inline-flex items-center gap-2 justify-center">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
          Map
        </button>
      </div>

      {mappings.length > 0 && (
        <div className="divide-y divide-border border-t border-border">
          {mappings.map((m) => (
            <div key={m.category} className="flex items-center justify-between py-2.5 text-sm" data-testid={`qbo-mapping-${m.category}`}>
              <div className="flex items-center gap-3">
                <span className="font-medium text-ink">{m.category}</span>
                <span className="text-ink-lighter">→</span>
                <span className="text-ink">{m.qbo_account_name}</span>
                {m.qbo_account_type && <span className="text-xs text-ink-lighter">({m.qbo_account_type})</span>}
              </div>
              <button onClick={() => remove(m.category)} data-testid={`qbo-mapping-delete-${m.category}`}
                className="text-ink-lighter hover:text-destructive">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
